import React, { useState, useCallback, useEffect } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { RefreshCw, AlertCircle, Plus, Search, X, Info, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import config from '@/config';

// Node Selection Modal Component
function NodeSelectionModal({ isOpen, onClose, nodeDefinitions, onSelectNode }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredNodes, setFilteredNodes] = useState(nodeDefinitions);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredNodes(nodeDefinitions);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = nodeDefinitions.filter(node => {
      const nameMatch = node.name?.toLowerCase().includes(term);
      const descMatch = node.description?.toLowerCase().includes(term);
      const inputKindMatch = node.input_kind?.toLowerCase().includes(term);
      const outputKindMatch = node.output_kind?.toLowerCase().includes(term);
      
      // Search in input_schema
      const inputSchemaMatch = node.input_schema?.some(input => 
        input.name?.toLowerCase().includes(term) || 
        input.type?.toLowerCase().includes(term)
      );
      
      // Search in output_schema
      const outputSchemaMatch = node.output_schema?.some(output => 
        output.name?.toLowerCase().includes(term) || 
        output.type?.toLowerCase().includes(term)
      );

      return nameMatch || descMatch || inputKindMatch || outputKindMatch || inputSchemaMatch || outputSchemaMatch;
    });

    setFilteredNodes(filtered);
  }, [searchTerm, nodeDefinitions]);

  const handleSelectNode = (node) => {
    onSelectNode(node);
    onClose();
    setSearchTerm('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Plus className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">Select Workflow Node</h2>
              <p className="text-blue-100 text-sm mt-1">
                Choose a node type to add to your workflow
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, description, input/output types..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
          </div>
          <div className="mt-2 text-sm text-gray-600">
            Showing {filteredNodes.length} of {nodeDefinitions.length} nodes
          </div>
        </div>

        {/* Node List */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredNodes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Search className="w-16 h-16 mb-3" />
              <p className="text-lg font-medium">No nodes found</p>
              <p className="text-sm">Try adjusting your search term</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {filteredNodes.map((node) => (
                <div
                  key={node.id}
                  onClick={() => handleSelectNode(node)}
                  className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:shadow-lg transition-all cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Node Name */}
                      <h3 className="font-bold text-gray-900 text-base mb-1">
                        {node.name}
                      </h3>
                      
                      {/* Description */}
                      {node.description && (
                        <p className="text-sm text-gray-600 mb-3">
                          {node.description}
                        </p>
                      )}

                      {/* Input/Output Kind Tags */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {node.input_kind && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                            <span className="font-semibold">Input:</span> {node.input_kind}
                          </span>
                        )}
                        {node.output_kind && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                            <span className="font-semibold">Output:</span> {node.output_kind}
                          </span>
                        )}
                      </div>

                      {/* Input/Output Schema Grid */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* Inputs */}
                        <div>
                          <div className="text-xs font-semibold text-blue-700 mb-2">
                            Inputs ({node.input_schema?.length || 0})
                          </div>
                          {node.input_schema && node.input_schema.length > 0 ? (
                            <div className="space-y-1">
                              {node.input_schema.map((input, idx) => (
                                <div key={idx} className="bg-blue-50 rounded px-2 py-1">
                                  <div className="text-xs font-medium text-gray-900 flex items-center gap-1">
                                    {input.name}
                                    {input.required && <span className="text-red-500">*</span>}
                                    {input.allow_manual_input && (
                                      <span className="ml-auto text-purple-600" title="Manual input allowed">✏️</span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-600">{input.type}</div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400 italic">No inputs</div>
                          )}
                        </div>

                        {/* Outputs */}
                        <div>
                          <div className="text-xs font-semibold text-green-700 mb-2">
                            Outputs ({node.output_schema?.length || 0})
                          </div>
                          {node.output_schema && node.output_schema.length > 0 ? (
                            <div className="space-y-1">
                              {node.output_schema.map((output, idx) => (
                                <div key={idx} className="bg-green-50 rounded px-2 py-1">
                                  <div className="text-xs font-medium text-gray-900">
                                    {output.name}
                                  </div>
                                  <div className="text-xs text-gray-600">{output.type}</div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400 italic">No outputs</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Add Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectNode(node);
                      }}
                      className="flex-shrink-0 p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Settings Node Component - for glasses_get_settings
const SettingsNode = ({ data }) => {
  const { nodeDef, instanceId, selectedSetting, settingOutputs, onSettingSelect } = data;
  const { authenticatedFetch } = useAuth();
  const [availableSettings, setAvailableSettings] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState(null);

  // Fetch available settings when component mounts
  useEffect(() => {
    const fetchSettings = async () => {
      setLoadingSettings(true);
      setSettingsError(null);
      
      try {
        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/workflows2/settings/list`
        );
        
        if (!response.ok) {
          throw new Error(`Failed to fetch settings: ${response.status}`);
        }
        
        const settings = await response.json();
        setAvailableSettings(settings);
      } catch (err) {
        console.error('Error fetching settings:', err);
        setSettingsError(err.message);
      } finally {
        setLoadingSettings(false);
      }
    };

    fetchSettings();
  }, [authenticatedFetch]);

  const handleSettingChange = async (settingId) => {
    if (!settingId) return;

    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/workflows2/settings/${settingId}`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch setting details: ${response.status}`);
      }
      
      const settingData = await response.json();
      // settingData should have format: { outputs: [{ id, kind, value }, ...] }
      onSettingSelect(instanceId, settingId, settingData.outputs);
    } catch (err) {
      console.error('Error fetching setting details:', err);
    }
  };

  return (
    <div className="shadow-lg rounded-lg bg-white border-2 border-purple-400 min-w-[280px] relative">
      {/* Header */}
      <div className="px-4 py-3 bg-purple-100 border-b border-purple-300 rounded-t-lg">
        <div className="font-bold text-sm text-purple-900">{nodeDef.name}</div>
        <div className="text-xs text-purple-600 truncate">ID: {instanceId}</div>
      </div>
      
      {/* Settings Selection */}
      <div className="px-4 py-3">
        <div className="text-xs font-semibold text-purple-600 mb-2">Select Setting</div>
        
        {loadingSettings ? (
          <div className="flex items-center gap-2 text-gray-500 text-xs">
            <RefreshCw className="w-3 h-3 animate-spin" />
            <span>Loading settings...</span>
          </div>
        ) : settingsError ? (
          <div className="flex items-center gap-2 text-red-500 text-xs">
            <AlertCircle className="w-3 h-3" />
            <span>{settingsError}</span>
          </div>
        ) : (
          <select
            value={selectedSetting || ''}
            onChange={(e) => handleSettingChange(e.target.value)}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
            onClick={(e) => e.stopPropagation()}
          >
            <option value="">-- Select a setting --</option>
            {availableSettings.map((setting) => (
              <option key={setting.id} value={setting.id}>
                {setting.name || setting.id}
              </option>
            ))}
          </select>
        )}
      </div>
      
      {/* Dynamic Outputs */}
      {settingOutputs && settingOutputs.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-200">
          <div className="text-xs font-semibold text-green-600 mb-2">Outputs</div>
          {settingOutputs.map((output, idx) => (
            <div key={output.id || idx} className="relative mb-2 pr-3">
              <Handle
                type="source"
                position={Position.Right}
                id={`${instanceId}-output-${output.id}`}
                style={{ right: '-8px', background: '#10b981', width: '10px', height: '10px' }}
              />
              <div className="text-xs text-gray-700 text-right bg-green-50 rounded p-2">
                <div className="font-medium">{output.id}</div>
                <div className="text-gray-500">Kind: {output.kind}</div>
                <div className="text-green-700 font-mono mt-1">
                  Value: {JSON.stringify(output.value)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Custom node component
const CustomNode = ({ data }) => {
  const { nodeDef, instanceId, inputValues, onInputChange, presentInputValues = {}, presentOutputValues = {} } = data;
  
  console.log(`Node ${instanceId} presentInputValues:`, presentInputValues);
  console.log(`Node ${instanceId} presentOutputValues:`, presentOutputValues);
  
  return (
    <div className="shadow-lg rounded-lg bg-white border-2 border-gray-300 min-w-[280px] relative">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-100 border-b border-gray-300 rounded-t-lg">
        <div className="font-bold text-sm text-gray-800">{nodeDef.name}</div>
        <div className="text-xs text-gray-500 truncate">ID: {instanceId}</div>
      </div>
      
      {/* Content in 2 columns */}
      <div className="grid grid-cols-2 gap-4 px-4 py-3">
        {/* Left column - Inputs */}
        <div>
          <div className="text-xs font-semibold text-blue-600 mb-2">Inputs</div>
          {nodeDef.input_schema && nodeDef.input_schema.length > 0 ? (
            nodeDef.input_schema.map((input, idx) => {
              // Get the present value from upstream node
              const presentValue = presentInputValues[input.id];
              const hasConnectedValue = presentValue !== undefined && presentValue !== null;
              
              return (
                <div key={input.id || idx} className="relative mb-3 pl-3">
                  <Handle
                    type="target"
                    position={Position.Left}
                    id={`${instanceId}-input-${input.id}`}
                    style={{ left: '-8px', background: '#3b82f6', width: '10px', height: '10px' }}
                  />
                  <div className="text-xs text-gray-700">
                    {/* Always show title as small uppercase */}
                    <div className="text-[10px] font-semibold text-blue-700 uppercase mb-0.5">
                      {input.name}
                    </div>
                    
                    {/* Show connected value from upstream node if exists - OVERRIDES manual input */}
                    {hasConnectedValue ? (
                      <div className="font-mono text-xs font-semibold text-blue-900 bg-blue-100 px-2 py-1 rounded mb-1 break-all border border-blue-300">
                        {typeof presentValue === 'string' 
                          ? `"${presentValue}"` 
                          : typeof presentValue === 'object'
                          ? JSON.stringify(presentValue)
                          : String(presentValue)}
                      </div>
                    ) : input.allow_manual_input ? (
                      // Only show manual input if NO connected value exists
                      <div className="mt-1">
                        {input.type === 'number' ? (
                          <input
                            type="number"
                            placeholder={input.default !== undefined ? String(input.default) : 'Enter value'}
                            value={inputValues?.[input.id] || ''}
                            onChange={(e) => onInputChange(input.id, e.target.value)}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : input.type === 'boolean' ? (
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={inputValues?.[input.id] || false}
                              onChange={(e) => onInputChange(input.id, e.target.checked)}
                              className="w-3 h-3"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="text-xs text-gray-600">Enable</span>
                          </label>
                        ) : (
                          <input
                            type="text"
                            placeholder={input.default || 'Enter value'}
                            value={inputValues?.[input.id] || ''}
                            onChange={(e) => onInputChange(input.id, e.target.value)}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </div>
                    ) : null}
                    
                    {/* Show type info */}
                    {!hasConnectedValue && (
                      <div className="text-gray-400 text-[10px]">
                        {input.type}
                        {input.required && <span className="text-red-500 ml-1">*</span>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-xs text-gray-400 italic">No inputs</div>
          )}
        </div>
        
        {/* Right column - Outputs */}
        <div>
          <div className="text-xs font-semibold text-green-600 mb-2">Outputs</div>
          {nodeDef.output_schema && nodeDef.output_schema.length > 0 ? (
            nodeDef.output_schema.map((output, idx) => {
              const presentValue = presentOutputValues[output.id];
              const hasValue = presentValue !== undefined && presentValue !== null;
              
              return (
                <div key={output.id || idx} className="relative mb-2 pr-3">
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`${instanceId}-output-${output.id}`}
                    style={{ right: '-8px', background: '#10b981', width: '10px', height: '10px' }}
                  />
                  <div className="text-xs text-gray-700 text-right">
                    <div className="text-[10px] font-semibold text-green-700 uppercase mb-0.5">
                      {output.name}
                    </div>
                    {hasValue && (
                      <div className="font-mono text-xs font-semibold text-green-900 bg-green-100 px-2 py-1 rounded mb-1 break-all border border-green-300">
                        {typeof presentValue === 'string' 
                          ? `"${presentValue}"` 
                          : typeof presentValue === 'object'
                          ? JSON.stringify(presentValue)
                          : String(presentValue)}
                      </div>
                    )}
                    <div className="text-gray-400 text-[10px]">{output.type}</div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-xs text-gray-400 italic text-right">No outputs</div>
          )}
        </div>
      </div>
    </div>
  );
};

const nodeTypes = {
  custom: CustomNode,
  settings: SettingsNode,
};

export default function DynamicNodeSystem() {
  const { authenticatedFetch } = useAuth();
  
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [nodeInstances, setNodeInstances] = useState({});
  const [nodeDefinitions, setNodeDefinitions] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dependencyTreeOpen, setDependencyTreeOpen] = useState(false);
  
  // Template metadata
  const [templateTitle, setTemplateTitle] = useState('');
  const [templateIdentifier, setTemplateIdentifier] = useState('');
  
  // Fetch workflow items from backend
  useEffect(() => {
    const fetchWorkflowItems = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/workflows2/workflow-items/nodes`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setNodeDefinitions(data);
      } catch (err) {
        console.error('Error fetching workflow items:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflowItems();
  }, [authenticatedFetch]);
  
  const onConnect = useCallback(
    (params) => {
      // Prevent self-connections (loop to same node)
      if (params.source === params.target) {
        alert('Cannot connect a node to itself!');
        return;
      }

      // Track field-level connections
      const sourceNode = nodes.find(n => n.id === params.source);
      const targetNode = nodes.find(n => n.id === params.target);
      
      if (sourceNode && targetNode) {
        // Extract the output_id and input_id from the handle IDs
        const sourceOutputId = params.sourceHandle?.replace(`${params.source}-output-`, '');
        const targetInputId = params.targetHandle?.replace(`${params.target}-input-`, '');
        
        // For settings nodes, we need to check the dynamic output schema
        let outputSchema, inputSchema;
        
        if (sourceNode.type === 'settings' && sourceNode.data.settingOutputs) {
          outputSchema = sourceNode.data.settingOutputs.find(o => o.id === sourceOutputId);
        } else {
          outputSchema = sourceNode.data.nodeDef.output_schema?.find(o => o.id === sourceOutputId);
        }
        
        inputSchema = targetNode.data.nodeDef.input_schema?.find(i => i.id === targetInputId);
        
        // Validate that types match
        if (outputSchema && inputSchema) {
          const outputType = outputSchema.type || outputSchema.kind;
          const inputType = inputSchema.type;
          
          if (outputType !== inputType) {
            alert(`Cannot connect: Output type "${outputType}" does not match input type "${inputType}"`);
            return;
          }
        }
        
        console.log('Connection created:', {
          source_instance_id: params.source,
          source_output_id: sourceOutputId,
          target_instance_id: params.target,
          target_input_id: targetInputId,
          type: outputSchema?.type || outputSchema?.kind
        });
      }
      
      setEdges((eds) => addEdge({ ...params, animated: true }, eds));
    },
    [setEdges, nodes]
  );

  const handleInputChange = useCallback((instanceId, inputId, value) => {
    // Update the node instances with the manual input value
    setNodeInstances((prev) => ({
      ...prev,
      [instanceId]: {
        ...prev[instanceId],
        manualInputValues: {
          ...prev[instanceId]?.manualInputValues || {},
          [inputId]: value
        }
      }
    }));

    // Update the node data to trigger re-render
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === instanceId) {
          return {
            ...node,
            data: {
              ...node.data,
              inputValues: {
                ...node.data.inputValues,
                [inputId]: value
              },
              onInputChange: node.data.onInputChange // Preserve the callback
            }
          };
        }
        return node;
      })
    );
  }, [setNodes, setNodeInstances]);

  const handleSettingSelect = useCallback((instanceId, settingId, settingOutputs) => {
    // Update node instances with selected setting
    setNodeInstances((prev) => ({
      ...prev,
      [instanceId]: {
        ...prev[instanceId],
        selectedSetting: settingId,
        settingOutputs: settingOutputs,
        outputs: settingOutputs.reduce((acc, output) => {
          acc[output.id] = output.value;
          return acc;
        }, {})
      }
    }));

    // Update the node data to display the selected setting and outputs
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === instanceId) {
          return {
            ...node,
            data: {
              ...node.data,
              selectedSetting: settingId,
              settingOutputs: settingOutputs
            }
          };
        }
        return node;
      })
    );
    
    // Immediately propagate values to connected downstream nodes
    propagateValues();
  }, [setNodes, setNodeInstances]);

  // Function to propagate values through the graph in real-time
  const propagateValues = useCallback(() => {
    // Build input connections map
    const inputConnections = {};
    
    edges.forEach(edge => {
      const sourceOutputId = edge.sourceHandle?.replace(`${edge.source}-output-`, '');
      const targetInputId = edge.targetHandle?.replace(`${edge.target}-input-`, '');
      
      if (!inputConnections[edge.target]) {
        inputConnections[edge.target] = {};
      }
      inputConnections[edge.target][targetInputId] = {
        source_instance_id: edge.source,
        source_output_id: sourceOutputId
      };
    });

    // Calculate input values for each node based on upstream outputs
    setNodes((nds) =>
      nds.map((node) => {
        const connections = inputConnections[node.id] || {};
        const presentInputValues = {};
        
        Object.entries(connections).forEach(([targetInputId, connection]) => {
          const sourceNode = nds.find(n => n.id === connection.source_instance_id);
          if (sourceNode) {
            // Get output value from source node
            let outputValue;
            
            if (sourceNode.type === 'settings' && sourceNode.data.settingOutputs) {
              // For settings nodes, get value from settingOutputs
              const output = sourceNode.data.settingOutputs.find(o => o.id === connection.source_output_id);
              outputValue = output?.value;
            } else if (sourceNode.data.presentOutputValues) {
              // For regular nodes, get from presentOutputValues
              outputValue = sourceNode.data.presentOutputValues[connection.source_output_id];
            }
            
            if (outputValue !== undefined) {
              presentInputValues[targetInputId] = outputValue;
            }
          }
        });
        
        return {
          ...node,
          data: {
            ...node.data,
            presentInputValues
          }
        };
      })
    );
  }, [edges, setNodes]);

  // Propagate values whenever edges change (new connection made)
  useEffect(() => {
    if (edges.length > 0) {
      propagateValues();
    }
  }, [edges, propagateValues]);

  const addNodeInstance = useCallback((nodeDef) => {
    if (!nodeDef) return;
    
    const instanceId = `instance_${Date.now()}`;
    const isSettingsNode = nodeDef.identifier === 'glasses_get_settings';
    
    // Create the callback that will be used by this node
    const inputChangeCallback = (inputId, value) => handleInputChange(instanceId, inputId, value);
    const settingSelectCallback = (instId, settingId, settingOutputs) => 
      handleSettingSelect(instId, settingId, settingOutputs);
    
    const newNode = {
      id: instanceId,
      type: isSettingsNode ? 'settings' : 'custom',
      position: { x: Math.random() * 400, y: Math.random() * 300 },
      data: { 
        nodeDef, 
        instanceId,
        outputs: {},
        inputValues: {},
        presentInputValues: {},
        presentOutputValues: {},
        onInputChange: inputChangeCallback,
        ...(isSettingsNode && {
          onSettingSelect: settingSelectCallback,
          selectedSetting: null,
          settingOutputs: []
        })
      },
    };
    
    setNodes((nds) => [...nds, newNode]);
    setNodeInstances((prev) => ({
      ...prev,
      [instanceId]: {
        nodeDefId: nodeDef.id,
        nodeTypeId: nodeDef.id,
        outputs: {},
        inputConnections: {},
        manualInputValues: {},
        ...(isSettingsNode && {
          selectedSetting: null,
          settingOutputs: []
        })
      }
    }));
  }, [handleInputChange, handleSettingSelect, setNodes, setNodeInstances]);

  const simulateExecution = () => {
    // Build dependency graph with field-level tracking
    const dependencies = {};
    const inputConnections = {};
    
    edges.forEach(edge => {
      if (!dependencies[edge.target]) {
        dependencies[edge.target] = [];
      }
      dependencies[edge.target].push(edge.source);
      
      // Track field-level connections
      const sourceOutputId = edge.sourceHandle?.replace(`${edge.source}-output-`, '');
      const targetInputId = edge.targetHandle?.replace(`${edge.target}-input-`, '');
      
      if (!inputConnections[edge.target]) {
        inputConnections[edge.target] = {};
      }
      inputConnections[edge.target][targetInputId] = {
        source_instance_id: edge.source,
        source_output_id: sourceOutputId
      };
    });

    // First pass: Generate outputs for all nodes
    const newNodeInstances = { ...nodeInstances };
    nodes.forEach(node => {
      const nodeDef = node.data.nodeDef;
      
      // For settings nodes, use their existing outputs from selected setting
      if (node.type === 'settings') {
        // Keep existing outputs from settings
        newNodeInstances[node.id] = {
          ...newNodeInstances[node.id],
          dependsOn: dependencies[node.id] || [],
          inputConnections: inputConnections[node.id] || {}
        };
        return;
      }
      
      const outputs = {};
      
      // Generate mock outputs based on schema
      nodeDef.output_schema?.forEach(output => {
        if (output.type === 'string' || output.type.includes('id')) {
          outputs[output.id] = `${output.name}_from_${node.id}`;
        } else if (output.type === 'number') {
          outputs[output.id] = Math.floor(Math.random() * 100);
        } else if (output.type === 'boolean') {
          outputs[output.id] = Math.random() > 0.5;
        } else {
          outputs[output.id] = `mock_${output.type}`;
        }
      });
      
      newNodeInstances[node.id] = {
        ...newNodeInstances[node.id],
        outputs,
        dependsOn: dependencies[node.id] || [],
        inputConnections: inputConnections[node.id] || {}
      };
    });

    // Second pass: Calculate input values for each node based on upstream outputs
    nodes.forEach(node => {
      const connections = inputConnections[node.id] || {};
      const presentInputValues = {};
      
      Object.entries(connections).forEach(([targetInputId, connection]) => {
        const sourceNodeInstance = newNodeInstances[connection.source_instance_id];
        if (sourceNodeInstance && sourceNodeInstance.outputs) {
          presentInputValues[targetInputId] = sourceNodeInstance.outputs[connection.source_output_id];
        }
      });
      
      // Store present input values in node instance
      newNodeInstances[node.id] = {
        ...newNodeInstances[node.id],
        presentInputValues
      };
    });

    setNodeInstances(newNodeInstances);
    
    // Update all nodes with their present input and output values
    setNodes((nds) =>
      nds.map((node) => {
        const instance = newNodeInstances[node.id];
        return {
          ...node,
          data: {
            ...node.data,
            presentOutputValues: instance?.outputs || {},
            presentInputValues: instance?.presentInputValues || {}
          }
        };
      })
    );
  };

  const getDependencyTree = () => {
    const tree = [];
    Object.entries(nodeInstances).forEach(([instanceId, data]) => {
      const node = nodes.find(n => n.id === instanceId);
      if (node) {
        tree.push({
          id: instanceId,
          name: node.data.nodeDef.name,
          nodeTypeId: data.nodeTypeId,
          dependsOn: data.dependsOn || [],
          inputConnections: data.inputConnections || {},
          manualInputValues: data.manualInputValues || {},
          outputs: data.outputs || {},
          presentInputValues: data.presentInputValues || {},
          ...(node.type === 'settings' && {
            selectedSetting: data.selectedSetting,
            settingOutputs: data.settingOutputs
          })
        });
      }
    });
    return tree;
  };

  const exportWorkflowConfiguration = () => {
    const nodesList = Object.entries(nodeInstances).map(([instanceId, data]) => {
      const node = nodes.find(n => n.id === instanceId);
      const nodeConfig = {
        instance_id: instanceId,
        workflow_item_id: data.nodeTypeId,
        name: node?.data.nodeDef.name,
        input_connections: data.inputConnections || {},
        manual_input_values: data.manualInputValues || {}
      };
      
      // Add settings-specific data
      if (node?.type === 'settings') {
        nodeConfig.selected_setting = data.selectedSetting;
        nodeConfig.setting_outputs = data.settingOutputs;
      }
      
      return nodeConfig;
    });

    const positionsDict = {};
    nodes.forEach(node => {
      positionsDict[node.id] = {
        x: node.position.x,
        y: node.position.y
      };
    });

    const edgesList = edges.map(edge => ({
      source_instance_id: edge.source,
      source_output_id: edge.sourceHandle?.replace(`${edge.source}-output-`, ''),
      target_instance_id: edge.target,
      target_input_id: edge.targetHandle?.replace(`${edge.target}-input-`, '')
    }));

    const config = {
      nodes: nodesList,
      positions: positionsDict,
      edges: edgesList
    };
    
    console.log('Workflow Configuration:', JSON.stringify(config, null, 2));
    return config;
  };

  const handleCreateTemplate = () => {
    const workflowConfig = exportWorkflowConfiguration();
    
    const templateData = {
      title: templateTitle,
      identifier: `template_${templateIdentifier}`,
      workflow: workflowConfig,
      metadata: {
        nodeCount: nodes.length,
        edgeCount: edges.length,
        createdAt: new Date().toISOString()
      }
    };
    
    console.log('Creating Template:', JSON.stringify(templateData, null, 2));
    
    // TODO: Replace this with actual API call or callback to parent component
    alert(`Template "${templateTitle}" (template_${templateIdentifier}) created successfully!\n\nCheck console for full data.`);
    
    // Return the data for parent component to use
    return templateData;
  };

  return (
    <div className="w-full h-screen flex flex-col bg-gray-50">
      {/* Toolbar */}
      <div className="bg-white shadow-md p-4 flex items-center gap-4 z-10 flex-wrap">
        <div className="flex items-center gap-4 flex-1">
          <h1 className="text-xl font-bold text-gray-800">Template Builder</h1>
          
          {/* Template Title Input */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Title:</label>
            <input
              type="text"
              placeholder="Enter template title"
              value={templateTitle}
              onChange={(e) => setTemplateTitle(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
            />
          </div>
          
          {/* Template Identifier Input */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Identifier:</label>
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500">
              <span className="px-3 py-1.5 bg-gray-100 text-sm text-gray-600 font-mono border-r border-gray-300">
                template_
              </span>
              <input
                type="text"
                placeholder="identifier"
                value={templateIdentifier}
                onChange={(e) => setTemplateIdentifier(e.target.value)}
                className="px-3 py-1.5 text-sm focus:outline-none w-40"
              />
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
                {loading ? (
          <div className="flex items-center gap-2 text-gray-600">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="text-sm">Loading workflow items...</span>
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
          </div>
        ) : (
          <>
            <button
              onClick={() => setModalOpen(true)}
              disabled={nodeDefinitions.length === 0}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Node ({nodeDefinitions.length} available)
            </button>
            
            <button
              onClick={simulateExecution}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm font-medium flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Simulate Execution
            </button>
            
            <button
              onClick={handleCreateTemplate}
              disabled={!templateTitle || !templateIdentifier}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              Create Template
            </button>
          </>
        )}
        </div>
      </div>

      <div className="flex-1 flex relative">
        {/* React Flow Canvas */}
        <div className="flex-1">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{
              padding: 0.2,
              minZoom: 0.5,
              maxZoom: 1.5
            }}
            minZoom={0.1}
            maxZoom={2}
            defaultZoom={1}
          >
            <Controls />
            <MiniMap />
            <Background variant="dots" gap={12} size={1} />
          </ReactFlow>
        </div>

        {/* Collapsible Toggle Button (when panel is closed) */}
        {!dependencyTreeOpen && (
          <button
            onClick={() => setDependencyTreeOpen(true)}
            className="absolute top-4 right-4 p-2 bg-white border-2 border-gray-300 rounded-lg shadow-lg hover:bg-gray-50 transition-colors z-10"
            title="Open Dependency Tree"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700" />
          </button>
        )}

        {/* Dependency Tree Panel */}
        {dependencyTreeOpen && (
          <div className="w-80 bg-white border-l border-gray-200 flex flex-col shadow-lg">
            {/* Header with close button */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <h2 className="text-lg font-bold text-gray-800">Dependency Tree</h2>
              <button
                onClick={() => setDependencyTreeOpen(false)}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                title="Close panel"
              >
                <ChevronRight className="w-5 h-5 text-gray-700" />
              </button>
            </div>
            
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto p-4">
                    {getDependencyTree().length === 0 ? (
            <p className="text-sm text-gray-500">No nodes added yet</p>
          ) : (
            <div className="space-y-4">
              {getDependencyTree().map((item) => (
                <div key={item.id} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <div className="font-semibold text-sm text-gray-800">{item.name}</div>
                  <div className="text-xs text-gray-500 mb-2 break-all">{item.id}</div>
                  
                  {item.selectedSetting && (
                    <div className="mb-2">
                      <div className="text-xs font-semibold text-purple-600">Selected Setting:</div>
                      <div className="text-xs text-gray-700">{item.selectedSetting}</div>
                    </div>
                  )}
                  
                  {item.dependsOn.length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs font-semibold text-gray-600">Depends on:</div>
                      <ul className="text-xs text-gray-600 ml-3">
                        {item.dependsOn.map((depId) => {
                          const depNode = nodes.find(n => n.id === depId);
                          return (
                            <li key={depId} className="list-disc">
                              {depNode?.data.nodeDef.name} ({depId})
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  
                  {Object.keys(item.inputConnections).length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs font-semibold text-blue-600">Input Connections:</div>
                      <div className="text-xs text-gray-700 font-mono bg-white p-2 rounded mt-1">
                        {JSON.stringify(item.inputConnections, null, 2)}
                      </div>
                    </div>
                  )}
                  
                  {Object.keys(item.manualInputValues || {}).length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs font-semibold text-purple-600">Manual Input Values:</div>
                      <div className="text-xs text-gray-700 font-mono bg-purple-50 p-2 rounded mt-1">
                        {JSON.stringify(item.manualInputValues, null, 2)}
                      </div>
                    </div>
                  )}
                  
                  {Object.keys(item.outputs).length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-green-600">Outputs:</div>
                      <div className="text-xs text-gray-700 font-mono bg-white p-2 rounded mt-1">
                        {JSON.stringify(item.outputs, null, 2)}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
            </div>
          </div>
        )}
      </div>

      {/* Node Selection Modal */}
      <NodeSelectionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        nodeDefinitions={nodeDefinitions}
        onSelectNode={addNodeInstance}
      />
    </div>
  );
}